import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import type { OverviewResponse } from '../contract';
import { fmtKc, fmtNum } from '../format';
import { MetricRow, PctBadge } from './metric-row';
import styles from '../style.module.scss';
import more from '../style-more.module.scss';

type AgentRow = NonNullable<OverviewResponse['byAgent']>[number];

const agentColumns: ColumnsType<AgentRow> = [
  {
    title: 'Người đại diện',
    dataIndex: 'agent',
    render: (v: string) => <span className={styles.cellName}>{v || '—'}</span>,
  },
  {
    title: 'KC',
    dataIndex: 'diamonds',
    align: 'right',
    width: 120,
    render: (v: number) => <strong>{fmtKc(v)}</strong>,
  },
  {
    title: 'Creator',
    dataIndex: 'creators',
    align: 'right',
    width: 80,
    render: (v: number) => fmtNum(v),
  },
  {
    title: 'So tháng trước',
    dataIndex: 'pct',
    width: 120,
    render: (v: number | null) => <PctBadge pct={v} />,
  },
];

/** "Theo người đại diện" (06 E2) — backend chỉ gửi ở phạm vi toàn bộ; null → không vẽ. */
export const AgentTable = ({ ov }: { ov: OverviewResponse }) => {
  if (!ov.byAgent) return null;
  return (
    <section className={styles.card} aria-label="Theo người đại diện">
      <h2 className={styles.cardTitle}>Theo người đại diện (Top 10 KC)</h2>
      <Table<AgentRow>
        size="small"
        rowKey="agent"
        columns={agentColumns}
        dataSource={ov.byAgent}
        pagination={false}
        scroll={{ x: 420 }}
        locale={{ emptyText: 'Chưa có cột Đại diện trong file.' }}
      />
      <p className={more.note}>
        Tên = phần trước @ của email cột &quot;Đại diện&quot; trong file TikTok.
      </p>
    </section>
  );
};

/** "Thâm niên" + Creator mới + Creator LIVE mới + rời đại lý (06 E3, E5). */
export const CohortsCard = ({ ov }: { ov: OverviewResponse }) => {
  const cohorts = ov.cohorts || [];
  const max = cohorts.reduce((m, c) => Math.max(m, c.creators), 0);
  return (
    <section className={styles.card} aria-label="Thâm niên và biến động Creator">
      <h2 className={styles.cardTitle}>Thâm niên</h2>
      {cohorts.length ? (
        <ul className={more.barList}>
          {cohorts.map((c) => (
            <li key={c.key}>
              <span className={more.barLabel}>{c.label}</span>
              <span className={more.barTrack} aria-hidden>
                <span style={{ width: `${max ? (c.creators / max) * 100 : 0}%` }} />
              </span>
              <span className={more.barValue}>
                {fmtNum(c.creators)} Creator · {fmtKc(c.diamonds)} KC
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.muted}>Chưa có số ngày gia nhập.</p>
      )}
      <div className={more.divider} />
      <MetricRow
        label="Creator mới gia nhập"
        value={ov.newJoined ? fmtNum(ov.newJoined.creators) : '—'}
        hint={ov.newJoined ? `${fmtKc(ov.newJoined.diamonds)} KC` : undefined}
      />
      <MetricRow
        label="Creator LIVE mới (TikTok)"
        value={ov.newLiveCreators ? fmtNum(ov.newLiveCreators.creators) : '—'}
        hint={
          ov.newLiveCreators
            ? `${fmtKc(ov.newLiveCreators.diamonds)} KC`
            : 'Chỉ có khi tháng có file tháng TikTok.'
        }
      />
      {ov.departed && (
        <MetricRow
          label="Rời đại lý trong tháng"
          value={`${fmtNum(ov.departed.creators)} Creator · ${fmtKc(ov.departed.diamonds)} KC`}
          hint={
            <>
              Xem ở <Link to="/data-issues">Doanh thu → Vấn đề dữ liệu</Link>
            </>
          }
        />
      )}
    </section>
  );
};
